import React from 'react';
import { useNextCloseDate } from '../hooks/useNextCloseDate';

/**
 * CloseDateProvider Component - Provides close date based on last sale_date_setup record
 * @param {Object} props
 * @param {Function} props.children - Render function that receives { nextCloseDate, loading, error, refetch }
 * @param {boolean} props.autoFetch - Whether to automatically fetch on mount (default: true)
 * @param {Function} props.onDateChange - Callback when date changes
 * @param {string} props.fallbackDate - Fallback date if no records found (default: today)
 */
export const CloseDateProvider = ({ 
  children, 
  autoFetch = true, 
  onDateChange,
  fallbackDate 
}) => {
  const { nextCloseDate, loading, error, refetch } = useNextCloseDate(autoFetch);

  // Call onDateChange when date changes
  React.useEffect(() => {
    if (nextCloseDate && onDateChange) {
      onDateChange(nextCloseDate);
    }
  }, [nextCloseDate, onDateChange]);

  // Use fallback date if provided and no date found
  const effectiveDate = nextCloseDate || fallbackDate || new Date().toISOString().split('T')[0];

  return children({
    nextCloseDate: effectiveDate,
    loading,
    error,
    refetch
  });
};

/**
 * CloseDateDisplay Component - Simple display component for close date from sale_date_setup
 * @param {Object} props
 * @param {string} props.label - Label to display before the date
 * @param {string} props.className - CSS class name for styling
 * @param {string} props.format - Date format ('iso'|'locale'|'custom')
 * @param {Function} props.formatFunction - Custom format function
 * @param {boolean} props.showLoading - Whether to show loading indicator
 * @param {boolean} props.showError - Whether to show error message
 */
export const CloseDateDisplay = ({ 
  label = 'Close Date:', 
  className = '',
  format = 'iso',
  formatFunction,
  showLoading = true,
  showError = true
}) => {
  const formatDate = (dateString) => {
    if (formatFunction) {
      return formatFunction(dateString);
    }
    
    const date = new Date(dateString);
    
    switch (format) {
      case 'locale':
        return date.toLocaleDateString();
      case 'custom':
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      case 'iso':
      default:
        return dateString;
    }
  };

  return (
    <CloseDateProvider>
      {({ nextCloseDate, loading, error }) => (
        <div className={`close-date-display ${className}`}>
          {loading && showLoading && (
            <span className="text-muted">
              <i className="fas fa-spinner fa-spin"></i> Loading close date...
            </span>
          )}
          
          {error && showError && (
            <span className="text-warning">
              <i className="fas fa-exclamation-triangle"></i> {error}
            </span>
          )}
          
          {nextCloseDate && !loading && (
            <span>
              <strong>{label}</strong> {formatDate(nextCloseDate)}
            </span>
          )}
        </div>
      )}
    </CloseDateProvider>
  );
};

/**
 * CloseDateInput Component - Input field with close date from sale_date_setup as default value
 * @param {Object} props
 * @param {string} props.name - Input name attribute
 * @param {string} props.id - Input id attribute
 * @param {string} props.className - CSS class name for styling
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.readonly - Whether input is readonly
 * @param {Object} props.inputProps - Additional input properties
 */
export const CloseDateInput = ({ 
  name = 'closeDate',
  id,
  className = 'form-control',
  onChange,
  disabled = false,
  readonly = false,
  ...inputProps
}) => {
  return (
    <CloseDateProvider onDateChange={onChange}>
      {({ nextCloseDate, loading }) => (
        <input
          type="date"
          name={name}
          id={id || name}
          className={className}
          value={nextCloseDate || ''}
          onChange={onChange}
          disabled={disabled || loading}
          readOnly={readonly}
          {...inputProps}
        />
      )}
    </CloseDateProvider>
  );
};

export default CloseDateProvider;